import { useCallback } from 'react';
import { useStore } from 'react-redux';
import type { RootState } from '../store/store';
import { touchlessService } from '../services/api/touchlessService';
import { middlewareService } from '../services/middleware/middlewareService';
import {
  buildEnhancedIdCheckBody,
  describeEnhancedIdChecks,
  isEnhancedIdValidationOn,
} from '../utils/enhancedIdValidation';

// identityIds already sent, so a photo retake or a re-render doesn't send the
// same scan twice. Each ID retake is a new identityId.
const startedIdentityIds = new Set<string>();

/** Test hook: forget which scans were sent. */
export const resetEnhancedIdChecksForTests = () => startedIdentityIds.clear();

type EnhancedIdChecksState = Pick<RootState, 'app' | 'visitor'>;

// EN-5235. Contactless check-in and pre-arrival take the visitor photo after the
// ID, so, like the kiosk's check-in via ID, the checks start once per check-in,
// for the last scan, when the name and photo are both in hand: on the photo
// step's confirm, or on leaving the ID step when there is no photo step.
//
// Started, never awaited: check-in goes by validateId's answer, and the outcome
// only reaches the logs and the backend's check history. The visit must not
// link the scan before the backend reads it (it then answers
// scan-already-linked-to-visit), which is why this starts before Confirmation.
//
// Takes the state as it is now, not as the caller last rendered it: the ID step
// stores the new scan and leaves in the same handler.
export const startEnhancedIdChecks = (state: EnhancedIdChecksState): boolean => {
  if (!isEnhancedIdValidationOn(state.app?.config)) return false;

  const scans = state.visitor?.idValidationFields?.idValidatedModels ?? [];
  const identityId = scans[scans.length - 1]?.identityId;
  if (!identityId || startedIdentityIds.has(identityId)) return false;
  startedIdentityIds.add(identityId);

  const info = (state.visitor?.visitorinfo ?? {}) as Record<string, unknown>;
  const body = buildEnhancedIdCheckBody({
    attemptNumber: scans.length,
    visitorFullName: info.fullName,
    visitPhotoImage: info.visitPhotoImage,
  });

  void touchlessService
    .runEnhancedIdChecks(identityId, body)
    .then((response) => {
      middlewareService.logInfo('enhanced_id_checks', {
        identityId,
        attemptNumber: body.attemptNumber,
        withPhoto: !!body.visitPhotoImage,
        result: describeEnhancedIdChecks(response),
        identityStatus: response?.identityStatus ?? null,
      });
    })
    .catch((error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response?.status ?? null;
      middlewareService.logWarn('enhanced_id_checks_failed', { identityId, status });
    });
  return true;
};

export const useEnhancedIdChecks = () => {
  const store = useStore<RootState>();
  return {
    startEnhancedIdChecks: useCallback(() => startEnhancedIdChecks(store.getState()), [store]),
  };
};
